import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Color, Masks } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalWorkflowService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { first } from 'rxjs/operators';
import { TwoFactorService } from '../two-factor.service';

@Component({
    selector: 'edit-two-factor-verify-modal',
    template: `
        <form class="modal-content" [formGroup]="formGroup" (ngSubmit)="handleSubmit()">
            <div class="modal-header" [style.padding-top]="'3rem'">
                <h2 i18n>Edit Multi-Factor Authentication</h2>
                <lsq-button
                    appearance="transparent"
                    class="close"
                    ariaLabel="Close"
                    i18n-ariaLabel
                    (buttonClick)="modalWorkflowService.hideWorkflow()"
                >
                    <lsq-icon aria-hidden="true" [type]="icons.Close" [color]="colors.Black">
                    </lsq-icon>
                </lsq-button>
            </div>
            <div class="modal-body">
                <p>
                    <ng-container i18n>
                        Enter the one-time verification code that was sent via SMS to the mobile
                        number provided:
                    </ng-container>
                    <strong>{{
                        twoFactorService.userPhoneNumberToVerifyForm.get('phoneNumber').value
                            | phone
                    }}</strong>
                    <lsq-button
                        i18n
                        [style.margin-left]="'1rem'"
                        appearance="transparent"
                        buttonClass="btn-link-uppercase"
                        (buttonClick)="modalWorkflowService.previousStep()"
                    >
                        Edit
                    </lsq-button>
                </p>

                <div>
                    <lsq-input
                        [group]="formGroup"
                        formControlName="verificationCode"
                        [name]="'verification-code'"
                        [id]="'verification-code'"
                        [label]="'Enter Verification Code'"
                        i18n-label
                        [maxLength]="codeMaxLength"
                        [mask]="digitsOnlyMask"
                    >
                    </lsq-input>
                </div>
                <p
                    [style.padding-top]="'0'"
                    [style.margin-bottom]="'0'"
                    [style.margin-top]="'-0.5rem'"
                >
                    <ng-container i18n>Didn't receive the code?</ng-container>
                    <lsq-button
                        i18n
                        [style.margin-left]="'1rem'"
                        appearance="transparent"
                        buttonClass="btn-link-uppercase"
                        (buttonClick)="resendCode()"
                    >
                        Resend
                    </lsq-button>
                </p>
            </div>
            <div class="modal-footer">
                <div class="modal-footer-left">
                    <lsq-button
                        i18n
                        size="sm"
                        type="button"
                        appearance="secondary"
                        (buttonClick)="modalWorkflowService.hideWorkflow()"
                    >
                        Cancel
                    </lsq-button>
                </div>
                <div class="modal-footer-right">
                    <lsq-button
                        i18n
                        size="sm"
                        type="submit"
                        [disabled]="formGroup.invalid"
                        [submitting]="submitting"
                    >
                        Verify Identity
                    </lsq-button>
                </div>
            </div>
        </form>
    `,
    styleUrls: ['./edit-two-factor-verify-modal.component.scss']
})
export class EditTwoFactorVerifyModalComponent {
    public readonly icons = Icons;
    public readonly colors = Color;
    public readonly codeMaxLength = 6;
    public readonly digitsOnlyMask = Masks.DigitsOnlyMask(this.codeMaxLength);
    public readonly _invalidVerificationCodeCopy = this._i18n({
        value: 'Invalid passcode'
    });
    public readonly _expiredVerificationCodeCopy = this._i18n({
        value: 'Passcode expired'
    });

    public submitting = false;
    public codeIsInvalid = false;
    public codeHasExpired = false;
    public formGroup = new FormGroup({
        verificationCode: new FormControl('', [
            Validators.required,
            LsqValidators.exactLength(this.codeMaxLength),
            () =>
                this.codeIsInvalid
                    ? { codeIsInvalid: { message: this._invalidVerificationCodeCopy } }
                    : null,
            () =>
                this.codeHasExpired
                    ? { codeHasExpired: { message: this._expiredVerificationCodeCopy } }
                    : null
        ])
    });

    constructor(
        private _i18n: I18n,
        public modalWorkflowService: ModalWorkflowService,
        public twoFactorService: TwoFactorService
    ) {}

    public async handleSubmit(): Promise<void> {
        if (this.formGroup.invalid) {
            return;
        }
        this.submitting = true;
        try {
            await this.twoFactorService.verifyIdentity(
                this.formGroup.get('verificationCode').value
            );
            this.modalWorkflowService.nextStep();
        } catch (error) {
            if (error.message?.startsWith('Verification code does not match')) {
                this.codeIsInvalid = true;
            } else if (error.message?.startsWith('Verification code has expired')) {
                this.codeHasExpired = true;
            }
            this.formGroup.get('verificationCode').updateValueAndValidity();
            this.formGroup
                .get('verificationCode')
                .valueChanges.pipe(first())
                .subscribe(() => {
                    this.codeIsInvalid = false;
                    this.codeHasExpired = false;
                    this.formGroup
                        .get('verificationCode')
                        .updateValueAndValidity({ emitEvent: false });
                });
        } finally {
            this.submitting = false;
        }
    }

    public async resendCode(): Promise<void> {
        this.codeIsInvalid = false;
        this.codeHasExpired = false;
        this.formGroup.setValue({ verificationCode: '' });
        await this.twoFactorService.sendIdentityVerificationToPhone();
    }
}
